// src/Components/Partials/Sidebar.tsx
import React, { useState, useCallback, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { FiHome, FiFileText, FiCopy, FiZap, FiUser } from "react-icons/fi";
import type { IconType } from "react-icons";
import "../Partials/Sidebar.css";
import LogoWithName from "../../Assets/Images/napkin-logo.png";

const iconStyle: React.CSSProperties = { fontSize: 18, marginRight: 8, flexShrink: 0 };

/** Wrapper to avoid react-icons JSX component type mismatch with React 19 */
function Icon({ icon: I, style }: { icon: IconType; style?: React.CSSProperties }) {
  return <>{I({ style })}</>;
}

interface SidebarProps {
  active?: string; // kept for compatibility, NavLink handles active state
}

function Sidebar(_props: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // Close drawer on route change
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when drawer is open
  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  const navLinks = (
    <nav className="dashboard-nav" aria-label="Main navigation">
      <NavLink
        to="/dashboard"
        className={({ isActive }) =>
          `dashboard-link ${isActive ? "active" : ""}`
        }
        end
      >
        <Icon icon={FiHome} style={iconStyle} />
        <span className="dashboard-link-label">Dashboard</span>
      </NavLink>

      <NavLink
        to="/document-status"
        className={({ isActive }) =>
          `dashboard-link ${isActive ? "active" : ""}`
        }
      >
        <Icon icon={FiFileText} style={iconStyle} />
        <span className="dashboard-link-label">Document status</span>
      </NavLink>

      <NavLink
        to="/templates"
        className={({ isActive }) =>
          `dashboard-link ${isActive ? "active" : ""}`
        }
      >
        <Icon icon={FiCopy} style={iconStyle} />
        <span className="dashboard-link-label">Templates</span>
      </NavLink>

      <NavLink
        to="/quick-send"
        className={({ isActive }) =>
          `dashboard-link ${isActive ? "active" : ""}`
        }
      >
        <Icon icon={FiZap} style={iconStyle} />
        <span className="dashboard-link-label">Quick Send</span>
      </NavLink>

      <NavLink
        to="/profile"
        className={({ isActive }) =>
          `dashboard-link ${isActive ? "active" : ""}`
        }
      >
        <Icon icon={FiUser} style={iconStyle} />
        <span className="dashboard-link-label">Profile</span>
      </NavLink>
    </nav>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="dashboard-sidebar">
        {/* Logo / brand */}
        <button
          className="dashboard-logo"
          onClick={() => navigate("/dashboard")}
          aria-label="Go to Dashboard"
          type="button"
        >
          <img src={LogoWithName} className="dashboard-logo-image" alt="Napkin" />
        </button>

        {navLinks}
      </aside>

      {/* Mobile hamburger toggle — visible only below 900px */}
      <button
        type="button"
        className="mobile-nav-toggle"
        onClick={openDrawer}
        aria-label="Open navigation menu"
      >
        <span className="mobile-nav-toggle-bar" />
        <span className="mobile-nav-toggle-bar" />
        <span className="mobile-nav-toggle-bar" />
      </button>

      {/* Mobile drawer overlay */}
      <div
        className={`sidebar-drawer ${drawerOpen ? "sidebar-drawer-open" : ""}`}
        aria-hidden={!drawerOpen}
      >
        {/* Backdrop */}
        <div
          className="sidebar-drawer-backdrop"
          onClick={closeDrawer}
          aria-label="Close navigation menu"
        />

        {/* Drawer panel */}
        <div className="sidebar-drawer-panel">
          {/* Drawer header: logo + close button */}
          <div className="sidebar-drawer-header">
            <button
              className="dashboard-logo"
              onClick={() => {
                navigate("/dashboard");
                closeDrawer();
              }}
              aria-label="Go to Dashboard"
              type="button"
            >
              <img
                src={LogoWithName}
                className="dashboard-logo-image"
                alt="Napkin"
              />
            </button>

            <button
              type="button"
              className="sidebar-drawer-close"
              onClick={closeDrawer}
              aria-label="Close navigation menu"
            >
              <span className="sidebar-drawer-close-icon" aria-hidden="true">
                &times;
              </span>
            </button>
          </div>

          {/* Nav links (same as desktop) */}
          {navLinks}
        </div>
      </div>
    </>
  );
}

export default Sidebar;
